import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useEffect, useState } from "react";

/** Right-panel shell shared by the supplies shipping inspectors. */
export const SuppliesInspectorShell = memo(function SuppliesInspectorShell({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose?: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="3xl" p="3xl">
      <Flex align="flex-start" justify="space-between" py="sm">
        <Flex dir="column" gap="xxs">
          <Text fontSize="sm" fontWeight="semibold">
            {title}
          </Text>
          {subtitle && (
            <Text fontSize="xs" textColor="secondary">
              {subtitle}
            </Text>
          )}
        </Flex>
        {onClose && (
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        )}
      </Flex>
      {children}
    </Flex>
  );
});

/**
 * Blur-commit text input: committing per keystroke re-renders the inspector
 * and steals focus, so local state buffers edits until blur.
 */
export const SuppliesTextField = memo(function SuppliesTextField({
  label,
  value,
  onCommit,
  placeholder,
  description,
  disabled,
}: {
  label: string;
  value: string | undefined;
  onCommit: (value: string) => void;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
}) {
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  const handleBlur = useHandler(() => {
    if (localValue !== (value ?? "")) {
      onCommit(localValue);
    }
  });

  return (
    <RedoTextInput
      description={description}
      label={label}
      onBlur={handleBlur}
      placeholder={placeholder}
      setValue={setLocalValue}
      state={disabled ? "disabled" : undefined}
      value={localValue}
    />
  );
});

/**
 * Blur-commit percent input storing basis points, so a rate like 7.5% round
 * trips exactly and the evaluator never multiplies money by a float.
 */
export const SuppliesPercentField = memo(function SuppliesPercentField({
  label,
  basisPoints,
  onCommit,
  description,
  disabled,
}: {
  label: string;
  basisPoints: number | undefined;
  onCommit: (basisPoints: number) => void;
  description?: string;
  disabled?: boolean;
}) {
  const toText = (value: number | undefined): string =>
    value === undefined ? "" : String(value / 100);
  const [localValue, setLocalValue] = useState(toText(basisPoints));

  useEffect(() => {
    setLocalValue(toText(basisPoints));
  }, [basisPoints]);

  const handleBlur = useHandler(() => {
    const parsed = Number(localValue);
    if (localValue === "" || Number.isNaN(parsed) || parsed < 0) {
      setLocalValue(toText(basisPoints));
      return;
    }
    const rounded = Math.round(parsed * 100);
    if (rounded !== basisPoints) {
      onCommit(rounded);
    }
  });

  return (
    <RedoTextInput
      description={description}
      label={label}
      onBlur={handleBlur}
      placeholder="0"
      setValue={setLocalValue}
      state={disabled ? "disabled" : undefined}
      suffix="%"
      value={localValue}
    />
  );
});

/**
 * Blur-commit dollar input storing minor units, so the editor never shows a
 * cents integer and the step never stores a float.
 */
export const SuppliesMoneyField = memo(function SuppliesMoneyField({
  label,
  minorUnits,
  onCommit,
  description,
  disabled,
}: {
  label: string;
  minorUnits: number | undefined;
  onCommit: (minorUnits: number) => void;
  description?: string;
  disabled?: boolean;
}) {
  const toText = (value: number | undefined): string =>
    value === undefined ? "" : (value / 100).toFixed(2);
  const [localValue, setLocalValue] = useState(toText(minorUnits));

  useEffect(() => {
    setLocalValue(toText(minorUnits));
  }, [minorUnits]);

  const handleBlur = useHandler(() => {
    const parsed = Number(localValue);
    if (localValue === "" || Number.isNaN(parsed) || parsed < 0) {
      setLocalValue(toText(minorUnits));
      return;
    }
    const rounded = Math.round(parsed * 100);
    if (rounded !== minorUnits) {
      onCommit(rounded);
    }
  });

  return (
    <RedoTextInput
      description={description}
      label={label}
      onBlur={handleBlur}
      placeholder="0.00"
      prefix="$"
      setValue={setLocalValue}
      state={disabled ? "disabled" : undefined}
      value={localValue}
    />
  );
});
